<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SalesDeliverySummaryRockets from './SalesDeliverySummaryRockets.vue';
import SalesDeliverySummaryRankSkeleton from './SalesDeliverySummaryRankSkeleton.vue';

const props = defineProps({
  loading: { type: Boolean, default: false },
  summary: { type: Object, default: () => ({}) },
});
const { t } = useI18n();
const score = computed(() =>
  props.loading
    ? null
    : Math.min(5, Math.max(1, Number(props.summary.score || 1)))
);
const checks = computed(() =>
  props.loading ? [] : props.summary.checks || []
);
const completed = computed(() => checks.value.filter(item => item.complete));
const pending = computed(() => checks.value.filter(item => !item.complete));
const copy = computed(() => ({
  1: {
    description: t('SALES.DELIVERY.SUMMARY.SCORE.DESCRIPTIONS.ONE'),
    title: t('SALES.DELIVERY.SUMMARY.SCORE.TITLES.ONE'),
  },
  2: {
    description: t('SALES.DELIVERY.SUMMARY.SCORE.DESCRIPTIONS.TWO'),
    title: t('SALES.DELIVERY.SUMMARY.SCORE.TITLES.TWO'),
  },
  3: {
    description: t('SALES.DELIVERY.SUMMARY.SCORE.DESCRIPTIONS.THREE'),
    title: t('SALES.DELIVERY.SUMMARY.SCORE.TITLES.THREE'),
  },
  4: {
    description: t('SALES.DELIVERY.SUMMARY.SCORE.DESCRIPTIONS.FOUR'),
    title: t('SALES.DELIVERY.SUMMARY.SCORE.TITLES.FOUR'),
  },
  5: {
    description: t('SALES.DELIVERY.SUMMARY.SCORE.DESCRIPTIONS.FIVE'),
    title: t('SALES.DELIVERY.SUMMARY.SCORE.TITLES.FIVE'),
  },
}));
const labels = computed(() => ({
  seller_documents: t('SALES.DELIVERY.SUMMARY.SCORE.CHECKS.SELLER_DOCUMENTS'),
  buyer_documents: t('SALES.DELIVERY.SUMMARY.SCORE.CHECKS.BUYER_DOCUMENTS'),
  commission: t('SALES.DELIVERY.SUMMARY.SCORE.CHECKS.COMMISSION'),
  second_clause: t('SALES.DELIVERY.SUMMARY.SCORE.CHECKS.SECOND_CLAUSE'),
  emails: t('SALES.DELIVERY.SUMMARY.SCORE.CHECKS.EMAILS'),
}));
const label = item => labels.value[item.key] || item.key;
</script>

<template>
  <div class="mx-auto w-full max-w-[1160px] py-8 lg:py-14">
    <SalesDeliverySummaryRankSkeleton v-if="loading" />
    <div v-else class="grid gap-[15px]">
      <section
        class="rounded-lais border border-lais-border-soft bg-white p-5 sm:p-8 xl:px-11"
      >
        <SalesDeliverySummaryRockets
          :score="score"
          :aria-label="t('SALES.DELIVERY.SUMMARY.SCORE.ARIA', { score })"
        />
        <h2
          class="mt-2 font-redHatDisplay text-[32px] font-bold leading-9 text-[#6B9539]"
        >
          {{ copy[score].title }}
        </h2>
        <p class="mt-3 max-w-[1070px] text-xl leading-6 text-[#74848B]">
          {{ copy[score].description }}
        </p>
      </section>

      <section
        class="min-h-[28.75rem] rounded-lais border border-lais-border-soft bg-white p-5 sm:p-8 xl:px-10"
      >
        <p class="text-[24px] leading-[30px] text-[#464F53]">
          {{ t('SALES.DELIVERY.SUMMARY.SCORE.INTRO') }}
        </p>
        <div v-if="completed.length" class="mt-6">
          <h3
            class="mb-[19px] font-redHatDisplay text-xl font-bold leading-6 text-[#464F53]"
          >
            {{ t('SALES.DELIVERY.SUMMARY.SCORE.COMPLETED') }}
          </h3>
          <div
            v-for="item in completed"
            :key="item.key"
            :data-testid="`sales-delivery-summary-check-${item.key}`"
            class="mb-[19px] flex items-center gap-[17px]"
          >
            <span
              class="i-lucide-check size-5 shrink-0 text-[#01988C]"
              aria-hidden="true"
            />
            <span
              class="inline-flex h-6 items-center rounded-[4px] bg-[#E7F4D7] px-1.5 text-xs font-normal leading-[18px] text-[#6B9539]"
            >
              {{ t('SALES.DELIVERY.SUMMARY.SCORE.ROCKET') }}
            </span>
            <p class="text-base leading-6 text-[#464F53] line-through">
              {{ label(item) }}
            </p>
          </div>
        </div>
        <div v-if="pending.length" class="mt-6">
          <h3
            class="mb-[19px] font-redHatDisplay text-xl font-bold leading-6 text-[#464F53]"
          >
            {{ t('SALES.DELIVERY.SUMMARY.SCORE.MISSING') }}
          </h3>
          <div
            v-for="item in pending"
            :key="item.key"
            :data-testid="`sales-delivery-summary-check-${item.key}`"
            class="mb-[19px] flex items-center gap-[17px] pl-[37px]"
          >
            <span
              class="inline-flex h-6 items-center rounded-[4px] bg-[#E7F4D7] px-1.5 text-xs font-normal leading-[18px] text-[#6B9539]"
            >
              {{ t('SALES.DELIVERY.SUMMARY.SCORE.ROCKET') }}
            </span>
            <p class="text-base font-bold leading-6 text-[#464F53]">
              {{ label(item) }}
            </p>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>
